import apiClient from './api';

// ── Types ──

/**
 * One line inside a shared excerpt.
 *
 * `line_number` is the position inside its own source file, which is the only
 * stable address a line has. The viewer's mark set indexes into a merged,
 * timestamp-sorted array whose numbering depends on which files happen to be
 * loaded — sending those indices would resolve to different lines for the
 * recipient, silently.
 */
export interface LogShareLine {
  file: string;
  inner_file?: string;
  line_number: number;
  text: string;
  /** False for the context lines carried around a marked one. */
  marked: boolean;
}

/** Viewer reference to a source file, kept so the excerpt can be reopened in full. */
export interface LogShareFile {
  display_name: string;
  url: string;
  is_zip: boolean;
}

export interface LogShare {
  id: string;
  code: string;
  device_id: string;
  note: string;
  /** Marked lines only — `snapshot` also holds surrounding context. */
  line_count: number;
  snapshot: LogShareLine[];
  files: LogShareFile[];
  created_by: string;
  created_at: string;
  device_number?: string;
  device_serial?: string;
  device_machine_id?: string;
  /** False once the source files have rotated out of the archive. */
  files_available: boolean;
}

export interface CreateLogShareRequest {
  device_id: string;
  note: string;
  lines: LogShareLine[];
  files: LogShareFile[];
}

/** How many lines either side of a marked line travel with it. */
export const LOG_SHARE_CONTEXT = 3;

/** Server-side caps, mirrored here so the UI can refuse before the round trip. */
export const MAX_LOG_SHARE_MARKED = 200;
export const MAX_LOG_SHARE_LINES = 500;

// ── API ──

export const logShareService = {
  create: (body: CreateLogShareRequest) =>
    apiClient.post<{ code: string; id: string }>('/admin/log-shares', body),

  get: (code: string) => apiClient.get<LogShare>(`/admin/log-shares/${code}`),
};

/** Absolute URL for a share code, for the copy-to-clipboard button. */
export function logShareUrl(code: string): string {
  return `${window.location.origin}/logs/s/${code}`;
}
